<template>
  <div class="log-list">
    <!-- 筛选卡片 -->
    <el-card shadow="never" class="filter-card">
      <el-form :model="filters" size="small" class="filter-form">
        <el-form-item>
          <el-select v-model="filters.log_type" placeholder="日志类型" clearable>
            <el-option label="SAU" value="log" />
            <el-option label="RK3588" value="rk3588" />
            <el-option label="CAN" value="can" />
          </el-select>
        </el-form-item>
        <el-form-item>
          <el-input v-model="filters.vin" placeholder="车架VIN" clearable />
        </el-form-item>
        <el-form-item>
          <el-date-picker
            v-model="filters.log_date"
            type="date"
            placeholder="日志日期"
            value-format="yyyy-MM-dd"
          />
        </el-form-item>
        <el-form-item>
          <el-input v-model="filters.file_name" placeholder="文件名" clearable />
        </el-form-item>
        <el-form-item>
          <el-button type="primary" icon="el-icon-search" @click="handleSearch">查询</el-button>
          <el-button icon="el-icon-refresh" @click="handleReset">重置</el-button>
        </el-form-item>
      </el-form>
    </el-card>

    <!-- 分组表格卡片 -->
    <el-card shadow="never" class="table-card">
      <div slot="header" class="table-header">
        <span>日志分组</span>
        <div class="table-header-right">
          <span class="total-count">共 {{ total }} 组</span>
        </div>
      </div>

      <el-table
        v-loading="loading"
        :data="tableData"
        height="100%"
        stripe
        border
        size="small"
        style="width:100%"
        :default-sort="{ prop: 'latest_created_at', order: 'descending' }"
      >
        <!-- 展开行：展示该分组下的所有压缩包文件 -->
        <el-table-column type="expand" width="40">
          <template slot-scope="{ row: groupRow }">
            <div class="expand-inner">
              <el-table
                :data="groupRow.files"
                size="mini"
                stripe
                border
                style="width:100%"
              >
                <el-table-column prop="file_name" label="文件名" min-width="240" show-overflow-tooltip />
                <el-table-column label="文件大小" width="110" align="right">
                  <template slot-scope="{ row: fileRow }">
                    {{ formatSize(fileRow.file_size) }}
                  </template>
                </el-table-column>
                <el-table-column label="下载" width="60" align="center">
                  <template slot-scope="{ row: fileRow }">
                    <el-button
                      type="text"
                      icon="el-icon-download"
                      size="mini"
                      @click="handleDownload(fileRow)"
                    />
                  </template>
                </el-table-column>
              </el-table>
            </div>
          </template>
        </el-table-column>

        <el-table-column label="日志类型" width="100" align="center">
          <template slot-scope="{ row }">
            <el-tag v-if="row.log_type === 'rk3588'" type="warning" size="mini">RK3588</el-tag>
            <el-tag v-else-if="row.log_type === 'can'" type="success" size="mini">CAN</el-tag>
            <el-tag v-else size="mini">SAU</el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="vin" label="车架VIN" show-overflow-tooltip />
        <el-table-column prop="log_date" label="日志日期" width="120" align="center" />
        <el-table-column prop="file_count" label="文件数" width="80" align="center">
          <template slot-scope="{ row }">
            <span class="file-count">{{ row.file_count }}</span>
          </template>
        </el-table-column>
        <el-table-column label="总大小" width="110" align="right">
          <template slot-scope="{ row }">
            {{ formatSize(row.total_size) }}
          </template>
        </el-table-column>
        <el-table-column prop="latest_created_at" label="最近同步时间" width="160" align="center" sortable>
          <template slot-scope="{ row }">
            {{ formatTime(row.latest_created_at) }}
          </template>
        </el-table-column>
      </el-table>

      <div class="pagination-wrap">
        <el-pagination
          background
          :current-page="pagination.page"
          :page-sizes="[10, 20, 50, 100]"
          :page-size="pagination.pageSize"
          :total="total"
          layout="total, sizes, prev, pager, next, jumper"
          @size-change="handleSizeChange"
          @current-change="handlePageChange"
        />
      </div>
    </el-card>
  </div>
</template>

<script>
import { saveAs } from 'file-saver';
import { fetchLogGroups, fetchLogDownloadUrl } from '../api/log';
import { getNetworkMode } from '../utils/network';
import dayjs from 'dayjs';

export default {
  name: 'LogList',

  data() {
    return {
      loading: false,
      tableData: [],
      total: 0,
      pagination: {
        page: 1,
        pageSize: 20,
      },
      filters: {
        log_type: '',
        vin: '',
        log_date: '',
        file_name: '',
      },
    };
  },

  mounted() {
    this.loadData();
  },

  methods: {
    async loadData() {
      this.loading = true;
      try {
        const params = {
          page: this.pagination.page,
          pageSize: this.pagination.pageSize,
        };

        if (this.filters.log_type) params.log_type = this.filters.log_type;
        if (this.filters.vin) params.vin = this.filters.vin;
        if (this.filters.log_date) params.log_date = this.filters.log_date;
        if (this.filters.file_name) params.file_name = this.filters.file_name;

        const res = await fetchLogGroups(params);
        this.tableData = res.data.rows;
        this.total = res.data.total;
      } catch {
        this.tableData = [];
        this.total = 0;
      } finally {
        this.loading = false;
      }
    },

    handleSearch() {
      this.pagination.page = 1;
      this.loadData();
    },

    handleReset() {
      this.filters = {
        log_type: '',
        vin: '',
        log_date: '',
        file_name: '',
      };
      this.pagination.page = 1;
      this.loadData();
    },

    handleSizeChange(val) {
      this.pagination.pageSize = val;
      this.pagination.page = 1;
      this.loadData();
    },

    handlePageChange(val) {
      this.pagination.page = val;
      this.loadData();
    },

    /** 获取 MinIO 预签名下载链接并通过 file-saver 触发下载 */
    async handleDownload(fileRow) {
      const { object_name: objectName, file_name: fileName } = fileRow;

      const loading = this.$loading({
        lock: true,
        text: '正在下载...',
        spinner: 'el-icon-loading',
        background: 'rgba(0, 0, 0, 0.5)',
      });
      try {
        // 按网络通道（顶栏下拉）决定内网还是公网地址
        const internal = getNetworkMode() === 'internal';

        const res = await fetchLogDownloadUrl(objectName, { internal });
        if (!res.data || !res.data.url) return;

        // 从预签名 URL 拉取 blob 保存
        const blobRes = await fetch(res.data.url);
        if (!blobRes.ok) throw new Error(`下载失败: ${blobRes.statusText}`);
        const blob = await blobRes.blob();
        saveAs(blob, fileName);
      } catch {
        // 错误已由拦截器统一处理
      } finally {
        loading.close();
      }
    },

    formatSize(bytes) {
      if (!bytes && bytes !== 0) return '-';
      const n = parseInt(bytes);
      if (n < 1024) return `${n} B`;
      if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
      if (n < 1024 * 1024 * 1024) return `${(n / (1024 * 1024)).toFixed(1)} MB`;
      return `${(n / (1024 * 1024 * 1024)).toFixed(2)} GB`;
    },

    formatTime(val) {
      if (!val) return '-';
      return dayjs(val).format('YYYY-MM-DD HH:mm:ss');
    },
  },
};
</script>
<style scoped>
/* 页面撑满 main 区：顶栏与筛选卡固定，剩余高度全给表格卡 */
.log-list {
  height: 100%;
  min-height: 0;
  display: flex;
  flex-direction: column;
}

.filter-card {
  flex: 0 0 auto;
  margin-bottom: 16px;
  border-radius: 8px;
  border: 1px solid #e8eaed;
}

.filter-card:hover {
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.06);
}

.filter-form {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
  gap: 4px 16px;
}

.filter-form .el-form-item {
  margin-bottom: 0;
}

.filter-form .el-form-item .el-input,
.filter-form .el-form-item .el-select,
.filter-form .el-form-item .el-date-editor {
  width: 100%;
}

.table-card {
  flex: 1 1 auto;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  border-radius: 8px;
  border: 1px solid #e8eaed;
}

.table-card:hover {
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.06);
}

/* 卡片内容区接管剩余高度：表格自适应，分页始终贴底 */
.table-card ::v-deep .el-card__body {
  flex: 1 1 auto;
  min-height: 0;
  display: flex;
  flex-direction: column;
}

.table-card ::v-deep .el-card__body > .el-table {
  flex: 1 1 0;
  min-height: 0;
}

.table-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-weight: 600;
}

.total-count {
  color: #909399;
  font-size: 13px;
  font-weight: 400;
}

.pagination-wrap {
  flex: 0 0 auto;
  margin-top: 12px;
  text-align: right;
}

/* 展开区域 */
.expand-inner {
  padding: 12px 20px 4px;
}

/* 统计列 */
.file-count {
  font-weight: 600;
  color: #409EFF;
}
</style>
